import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileDown, Eye, Mail, Info } from "lucide-react";

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => THIS_YEAR - i);

export default function StatementsPage() {
  const [year, setYear] = useState(String(THIS_YEAR));
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState("all");
  const [rows, setRows] = useState(null);
  const [deductibleCats, setDeductibleCats] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get("/segments").then((r) => setSegments(r.data)).catch(() => {});
    api.get("/settings/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const { data } = await api.get("/statements/customers", {
        params: { year, segment_id: segmentId === "all" ? "" : segmentId },
      });
      setRows(data.customers);
      setDeductibleCats(data.deductible_categories);
      setSelected(new Set(data.customers.map((c) => c.id)));
    } catch (e) {
      toast.error(apiError(e));
      setRows([]);
    }
  }, [year, segmentId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((c) => c.id)));
  };

  const preview = async (id) => {
    try {
      const res = await api.get(`/statements/${id}/pdf`, { params: { year }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadZip = async () => {
    setBusy(true);
    try {
      const res = await api.post("/statements/generate",
        { year: Number(year), customer_ids: [...selected] }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statements-${year}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selected.size} statements are downloading as a ZIP.`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const emailStatements = async () => {
    try {
      const { data } = await api.post("/statements/email", { year: Number(year), customer_ids: [...selected] });
      setJob({ id: data.job_id, total: data.total, sent: 0, status: "processing", preview_mode: data.preview_mode });
      const poll = setInterval(async () => {
        const { data: j } = await api.get(`/statements/email-jobs/${data.job_id}`);
        setJob(j);
        if (j.status !== "processing") {
          clearInterval(poll);
          if (j.status === "completed") {
            toast.success(`${j.sent} statements ${j.preview_mode ? "prepared (preview mode — not actually emailed)" : "emailed"}. ${j.skipped} skipped (no email or unsubscribed).`);
          } else if (j.status === "completed_with_errors") {
            toast.warning(`${j.sent} statements emailed, ${j.failed} could not be sent. ${j.error || ""}`, { duration: 12000 });
          } else {
            toast.error(j.error ? `Statements could not be emailed. ${j.error}` : "Something went wrong while emailing statements.", { duration: 12000 });
          }
        }
      }, 900);
    } catch (e) { toast.error(apiError(e)); }
  };

  const withEmail = rows ? rows.filter((c) => selected.has(c.id) && c.email).length : 0;

  return (
    <div className="space-y-8" data-testid="statements-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Year-End Statements</h1>
        <p className="text-muted-foreground mt-2">
          Branded PDF statements with purchases and tax-deductible contributions listed separately.
        </p>
      </div>

      {emailStatus && !emailStatus.enabled && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-900" data-testid="email-preview-banner">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <p>Email sending is in <strong>preview mode</strong> — statements can be downloaded, but emails are simulated until a Resend API key is added under <Link to="/settings" className="underline font-medium">Settings → Email sending</Link>. Downloads work normally.</p>
        </div>
      )}

      <Card className="shadow-sm">
        <CardContent className="p-6 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label>Statement year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32" data-testid="statement-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Who to include</Label>
            <Select value={segmentId} onValueChange={setSegmentId}>
              <SelectTrigger className="w-64" data-testid="statement-segment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone with activity in {year}</SelectItem>
                {segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1" />
          <Button onClick={downloadZip} disabled={busy || selected.size === 0} data-testid="download-zip-btn">
            <FileDown className="h-4 w-4 mr-2" /> {busy ? "Preparing…" : `Download ${selected.size} PDFs (ZIP)`}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="secondary" disabled={withEmail === 0 || (job && job.status === "processing")} data-testid="email-statements-btn">
                <Mail className="h-4 w-4 mr-2" /> Email statements ({withEmail})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Email {year} statements to {withEmail} customers?</AlertDialogTitle>
                <AlertDialogDescription>
                  Each customer receives their own PDF statement. Customers without an email address or who unsubscribed are skipped automatically.
                  {emailStatus && !emailStatus.enabled && " Note: email is in preview mode, so sends will be simulated."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Not yet</AlertDialogCancel>
                <AlertDialogAction onClick={emailStatements} data-testid="confirm-email-statements-btn">Yes, email them</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {job && job.status === "processing" && (
        <Card className="shadow-sm" data-testid="email-job-progress">
          <CardContent className="p-6 space-y-3">
            <p className="font-medium">Emailing statements… {job.sent + (job.skipped || 0)} of {job.total}</p>
            <Progress value={job.total ? ((job.sent + (job.skipped || 0)) / job.total) * 100 : 5} />
          </CardContent>
        </Card>
      )}

      <p className="text-sm text-muted-foreground">
        Deductible categories: <strong>{deductibleCats.join(", ") || "none"}</strong> — change this in Settings.
      </p>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="statements-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={rows ? selected.size === rows.length && rows.length > 0 : false} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="hidden md:table-cell">Email</TableHead>
                <TableHead className="text-right">Purchases</TableHead>
                <TableHead className="text-right">Contributions</TableHead>
                <TableHead className="text-right">{year} total</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="no-activity-message">No customer activity found for {year}.</TableCell></TableRow>
              ) : (
                rows.map((c) => (
                  <TableRow key={c.id} data-testid={`statement-row-${c.id}`}>
                    <TableCell><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`select-${c.id}`} /></TableCell>
                    <TableCell className="py-4 font-medium">{c.name}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{c.email || "no email"}</TableCell>
                    <TableCell className="text-right">{fmtMoney(c.purchases_total)}</TableCell>
                    <TableCell className="text-right text-primary font-medium">{fmtMoney(c.deductible_total)}</TableCell>
                    <TableCell className="text-right font-semibold">{fmtMoney(c.year_total)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => preview(c.id)} data-testid={`preview-pdf-${c.id}`}>
                        <Eye className="h-4 w-4 mr-1" /> Preview
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
